// Las columnas de datos de la tabla de Outbound Loads.
//
// Fuera de aquí quedan tres cosas que no son "una columna más": la marca de
// fila y el chevron+número de carga (estructurales, en `FilaCargaSalida`,
// porque llevan el botón de desplegar) y las acciones (necesitan el callback
// de abrir el cajón de documentos, que vive en la página).
//
// Cut-off, ETD, contenedor, remolque y tractor NO están aquí: nacen NULL en el
// 100% de las cargas medidas, así que una columna para cada uno son cientos de
// guiones que no dicen nada y comen ancho. Van al desplegable
// (`DetalleCargaSalida`), declarados una vez como "sin dato en origen".
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import type { CargaSalida } from '@/types'
import { PickingBar } from '@/components/common/PickingBar'
import { PaletizadaBadge } from '@/components/pedidos-ventas/CargasPedido'
import { ChipModoTransporte } from './ChipModoTransporte'
import { fmtCajas, fmtPeso, fmtQty } from './formatoSalida'
import {
  clientesDistintos, fechaDe, lineasPalets, lineasPesoExpedicion, modoTransporteDe,
  NIVEL_CHIP, nivelDe, pickingPct, reservaDe, retrasoDe,
} from '@/utils/cargasSalida'
import { diasRetraso } from '@/utils/agruparPorFecha'

export interface ColumnaCargaSalida {
  key: string
  etiqueta: string
  /** Clase de ancho de Tailwind, para el `<colgroup>`. */
  ancho: string
  alinear?: 'left' | 'right'
  render: (c: CargaSalida, ctx: ContextoColumnas) => ReactNode
}

export interface ContextoColumnas {
  t: TFunction
  dark: boolean
  hoy: Date
}

const ALMACEN_CLS: Record<string, { dark: string; light: string }> = {
  VID:  { dark: 'bg-sky-500/15 text-sky-300',    light: 'bg-sky-100 text-sky-700' },
  GAVA: { dark: 'bg-violet-500/15 text-violet-300', light: 'bg-violet-100 text-violet-700' },
}

function AlmacenBadge({ almacen, dark }: { almacen?: string | null; dark: boolean }) {
  if (!almacen) return <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>
  const cls = ALMACEN_CLS[almacen] ?? { dark: 'bg-white/8 text-slate-300', light: 'bg-slate-100 text-slate-600' }
  return (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
      dark ? cls.dark : cls.light
    }`}>
      {almacen}
    </span>
  )
}

const PALETS_CHIP = {
  real:    { dark: 'bg-emerald-500/15 text-emerald-300', light: 'bg-emerald-100 text-emerald-700' },
  mixto:   { dark: 'bg-sky-500/15 text-sky-300',         light: 'bg-sky-100 text-sky-700' },
  mono:    { dark: 'bg-amber-500/15 text-amber-300',     light: 'bg-amber-100 text-amber-700' },
  sinDato: { dark: 'bg-white/8 text-slate-400',          light: 'bg-slate-100 text-slate-500' },
} as const

/** Celda de palets: una línea con chip por cada cifra que hay que enseñar. */
function CeldaPalets({ c, t, dark }: { c: CargaSalida; t: TFunction; dark: boolean }) {
  const lineas = lineasPalets(c)
  const chip = (clave: keyof typeof PALETS_CHIP) => (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
      dark ? PALETS_CHIP[clave].dark : PALETS_CHIP[clave].light
    }`}>
      {t(`cargasSalida.tabla.palets.${clave}`)}
    </span>
  )
  if (lineas.length === 0) {
    return (
      <span className="flex flex-col items-end gap-0.5">
        {chip('sinDato')}
        <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>
      </span>
    )
  }
  return (
    <span className="flex flex-col items-end gap-0.5">
      {lineas.map(l => (
        <span key={l.clave} className="flex items-center gap-1.5">
          {chip(l.clave)}
          <span className="tabular-nums">{fmtCajas(l.valor)}</span>
        </span>
      ))}
    </span>
  )
}

// Relleno sólido, como `NIVEL_CHIP`: el color sólo en el texto no se veía.
const PESO_CHIP = {
  real:     'bg-emerald-600 text-white',
  mixto:    'bg-sky-600 text-white',
  mono:     'bg-amber-600 text-white',
  sinPalet: 'bg-slate-600 text-white',   // completo: viaja sin palet
  sinDato:  'bg-slate-400 text-slate-900', // parcial: apagado, como el «sin dato» de palets
} as const

/** Celda «bruto + palet»: misma forma que `CeldaPalets`, una línea por cifra. */
function CeldaPesoExpedicion({ c, t, dark }: { c: CargaSalida; t: TFunction; dark: boolean }) {
  const lineas = lineasPesoExpedicion(c)
  if (lineas.length === 0) {
    return <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>
  }
  return (
    <span className={`flex flex-col items-end gap-0.5 text-[10px] ${dark ? 'text-slate-300' : 'text-slate-600'}`}
      title={t('cargasSalida.tabla.pesoExpedicion.tooltip')}
    >
      {lineas.map(l => (
        <span key={l.clave} className="flex items-center gap-1.5 whitespace-nowrap">
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${PESO_CHIP[l.clave]}`}>
            {t(`cargasSalida.tabla.pesoExpedicion.${l.clave}`)}
          </span>
          <span className="tabular-nums">
            {fmtPeso(l.valor)} <span className={dark ? 'text-slate-500' : 'text-slate-400'}>{t('salesOrders.picking.kg')}</span>
          </span>
        </span>
      ))}
    </span>
  )
}

/** «45 días de retraso» / «en 3 días» / «hoy» / nada si no hay fecha. */
function textoRelativo(c: CargaSalida, hoy: Date, t: TFunction): string | null {
  const f = fechaDe(c.fechaEnvioPrevista)
  if (!f) return null
  const dias = diasRetraso(f, hoy)
  if (dias > 0) return t('cargasSalida.fecha.retraso', { count: dias })
  if (dias < 0) return t('cargasSalida.fecha.enDias', { count: -dias })
  return t('cargasSalida.fecha.hoy')
}

export function columnasBase(): ColumnaCargaSalida[] {
  return [
    {
      key: 'clientePedido',
      etiqueta: 'cargasSalida.tabla.columnas.clientePedido',
      ancho: 'w-[200px]',
      render: (c, { t, dark }) => {
        const [primero, ...resto] = c.pedidos
        const varios = clientesDistintos(c)
        // Nombre arriba; sin nombre (8 de 639 cargas vivas) cae al código, que es
        // lo que se pintaba antes, y entonces no se repite abajo.
        const nombre = c.clienteNombre?.trim() || null
        return (
          <span className="flex flex-col gap-0.5 min-w-0">
            <span
              className={`text-[11px] font-semibold truncate ${dark ? 'text-slate-100' : 'text-slate-800'}`}
              title={nombre ?? c.cliente ?? undefined}
            >
              {nombre ?? c.cliente ?? '—'}
            </span>
            <span className="flex items-center gap-1">
              {nombre && c.cliente && (
                <span className={`font-mono text-[10px] shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {c.cliente}
                </span>
              )}
              <span
                className={`font-mono text-[10px] truncate ${dark ? 'text-slate-400' : 'text-slate-500'}`}
                title={c.pedidos.join('\n')}
              >
                {primero ?? '—'}
              </span>
              {resto.length > 0 && (
                <span
                  className={`text-[9px] font-bold tabular-nums shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`}
                  title={c.pedidos.join('\n')}
                >
                  +{resto.length}
                </span>
              )}
              {varios && (
                <span
                  className={`shrink-0 ${dark ? 'text-amber-400' : 'text-amber-600'}`}
                  title={t('cargasSalida.tabla.clientesDistintos')}
                >
                  <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M8 1.8L15 14H1L8 1.8z" strokeLinejoin="round" />
                    <path d="M8 6v3.5M8 11.6v.1" />
                  </svg>
                </span>
              )}
            </span>
          </span>
        )
      },
    },
    {
      key: 'almacen',
      etiqueta: 'cargasSalida.tabla.columnas.almacen',
      ancho: 'w-[76px]',
      render: (c, { dark }) => <AlmacenBadge almacen={c.almacen} dark={dark} />,
    },
    {
      // Clasificación comercial. El guion significa «no consta», no «ninguno»:
      // falta en una de cada cuatro cargas, y SIEMPRE en las que aún no tienen
      // líneas, que no están en la tabla materializada.
      key: 'grupoCliente',
      etiqueta: 'cargasSalida.tabla.columnas.grupoCliente',
      ancho: 'w-[112px]',
      render: (c, { dark }) => (
        c.grupoCliente
          ? <span className="text-[12px] font-medium">{c.grupoCliente}</span>
          : <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>
      ),
    },
    {
      key: 'incoterm',
      etiqueta: 'cargasSalida.tabla.columnas.incoterm',
      ancho: 'w-[84px]',
      render: (c, { dark }) => (
        c.incoterm
          ? <span className="text-[12px] font-mono">{c.incoterm}</span>
          : <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>
      ),
    },
    {
      key: 'fechaSalida',
      etiqueta: 'cargasSalida.tabla.columnas.fechaSalida',
      ancho: 'w-[110px]',
      render: (c, { t, dark, hoy }) => {
        const f = fechaDe(c.fechaEnvioPrevista)
        const relativo = textoRelativo(c, hoy, t)
        const retraso = retrasoDe(c, hoy)
        return (
          <span className="flex flex-col gap-0.5">
            <span className={`text-[11px] font-semibold tabular-nums ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
              {f ? f.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : t('cargasSalida.fecha.sinFecha')}
            </span>
            {relativo && (
              <span className={`text-[10px] ${
                retraso ? (dark ? 'text-red-400 font-semibold' : 'text-red-600 font-semibold') : (dark ? 'text-slate-500' : 'text-slate-400')
              }`}>
                {relativo}
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'transporte',
      etiqueta: 'cargasSalida.tabla.columnas.transporte',
      ancho: 'w-[112px]',
      // UN solo chip, con la contradicción ya resuelta en `modoTransporteDe`:
      // si la carga está marcada como aérea manda eso, y si no vale lo que diga
      // `modoTransporte`. Antes se pintaban los dos a la vez y se leía
      // «TERRESTRE ✈», que es exacto y no sirve de nada en una tabla.
      //
      // Celda en blanco cuando no hay ninguno de los dos —el 29 % de las cargas
      // vivas—: no hay TERRESTRE por defecto.
      render: (c, { dark }) => (
        <ChipModoTransporte modo={modoTransporteDe(c)} dark={dark} />
      ),
    },
    {
      key: 'cajas',
      etiqueta: 'cargasSalida.tabla.columnas.cajas',
      ancho: 'w-[64px]',
      alinear: 'right',
      render: c => fmtCajas(c.cajas),
    },
    {
      key: 'palets',
      etiqueta: 'cargasSalida.tabla.columnas.palets',
      ancho: 'w-[104px]',
      alinear: 'right',
      // Una columna, varias líneas, cada una con su chip: definitivo = una
      // línea; estimado = mixto y mono; sin dato = «SIN DATO» y guion (nunca 0).
      render: (c, { t, dark }) => <CeldaPalets c={c} t={t} dark={dark} />,
    },
    {
      key: 'pesos',
      etiqueta: 'cargasSalida.tabla.columnas.pesos',
      ancho: 'w-[130px]',
      alinear: 'right',
      render: (c, { t, dark }) => (
        <span className={`flex flex-col items-end gap-0.5 text-[10px] ${dark ? 'text-slate-300' : 'text-slate-600'}`}
          title={t('salesOrders.picking.pesoTooltip')}
        >
          <span>{fmtPeso(c.pesoNeto)} <span className={dark ? 'text-slate-500' : 'text-slate-400'}>{t('salesOrders.picking.kg')}</span></span>
          <span>{fmtPeso(c.pesoBruto)} <span className={dark ? 'text-slate-500' : 'text-slate-400'}>{t('salesOrders.picking.kg')}</span></span>
        </span>
      ),
    },
    {
      key: 'pesoExpedicion',
      etiqueta: 'cargasSalida.tabla.columnas.pesoExpedicion',
      ancho: 'w-[150px]',
      alinear: 'right',
      render: (c, { t, dark }) => <CeldaPesoExpedicion c={c} t={t} dark={dark} />,
    },
    {
      key: 'reserva',
      etiqueta: 'cargasSalida.tabla.columnas.reserva',
      // Ancho fijo y apilado en dos líneas: con una sola línea, una carga de
      // seis cifras («123.456 / 123.456») se pisaba con la columna de Picking
      // de al lado. Reservada arriba, solicitada debajo — mismo criterio que
      // `ReservaCarga`, sin abreviar ninguna cifra.
      ancho: 'w-[92px]',
      alinear: 'right',
      render: (c, { t, dark }) => {
        const { falta, completa } = reservaDe(c)
        const hueco = !completa
        return (
          <span
            className="flex flex-col gap-0.5"
            title={t(hueco ? 'salesOrders.picking.reservaTooltipHueco' : 'salesOrders.picking.reservaTooltip', {
              carga: c.carga,
              reservada: fmtQty(c.qtyReservada),
              solicitada: fmtQty(c.qtySolicitada),
              falta: fmtQty(falta),
            })}
          >
            <span className={`text-[11px] font-semibold tabular-nums text-right whitespace-nowrap ${
              hueco ? (dark ? 'text-amber-300' : 'text-amber-700') : (dark ? 'text-slate-200' : 'text-slate-700')
            }`}>
              {fmtQty(c.qtyReservada)}
            </span>
            <span className={`text-[10px] tabular-nums text-right whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              / {fmtQty(c.qtySolicitada)}
            </span>
          </span>
        )
      },
    },
    {
      key: 'picking',
      etiqueta: 'cargasSalida.tabla.columnas.picking',
      // Mismo motivo que Reserva: las cifras apiladas arriba y la barra debajo,
      // a todo el ancho de la celda, en vez de compartir línea con la barra.
      ancho: 'w-[112px]',
      alinear: 'right',
      render: (c, { t, dark }) => (
        <span
          className="flex flex-col gap-0.5"
          title={t('salesOrders.picking.cargaTooltip', {
            carga: c.carga, picking: fmtQty(c.qtyPicking),
            solicitada: fmtQty(c.qtySolicitada), pendiente: fmtQty(c.qtyPendiente),
          })}
        >
          <span className={`text-[10px] font-semibold tabular-nums text-right whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
            {fmtQty(c.qtyPicking)} / {fmtQty(c.qtySolicitada)}
          </span>
          <PickingBar pct={pickingPct(c)} dark={dark} compact />
        </span>
      ),
    },
    {
      key: 'estado',
      etiqueta: 'cargasSalida.tabla.columnas.estado',
      ancho: 'w-[150px]',
      // El chip dice «¿puedo expedir esto?» (`nivelDe`) y NO es rojo nunca: el
      // rojo es de la chapa «EN RIESGO» junto al nº de carga, que mira la fecha.
      render: (c, { t, dark }) => {
        const nivel = nivelDe(c)
        return (
          <span className="flex items-center gap-1 flex-wrap">
            <span
              title={t(`cargasSalida.nivel.${nivel}Tooltip`)}
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
                dark ? NIVEL_CHIP[nivel].dark : NIVEL_CHIP[nivel].light
              }`}
            >
              {t(`cargasSalida.nivel.${nivel}`)}
            </span>
            {c.paletizada != null && <PaletizadaBadge paletizada={c.paletizada} dark={dark} />}
          </span>
        )
      },
    },
    {
      key: 'muelle',
      etiqueta: 'cargasSalida.tabla.columnas.muelle',
      ancho: 'w-[100px]',
      render: (c, { t, dark }) => (
        c.muelle
          ? <span className={dark ? 'text-slate-100' : 'text-slate-800'}>{c.muelle}</span>
          : <span className={`text-[10px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{t('cargasSalida.muelle.sinMuelle')}</span>
      ),
    },
  ]
}
